// src/components/LiveStreaming.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  FaPlay,
  FaPause,
  FaStepBackward,
  FaStepForward,
  FaInfinity,
  FaVolumeMute,
  FaVolumeUp,
  FaListUl,
  FaRandom,
  FaMusic,
  FaWindowMinimize,
  FaCross,
} from "react-icons/fa";
import "./LiveStreaming.css";
import SEO from "./SEO";
import localVideos from "../assets/livestreaming/videos.json";
import playlistsData from "../assets/livestreaming/playlists.json";
import SiteWatermark from "./SiteWatermark";
import { loadYouTubeIframeAPI } from "../utils/youtube";

const ALL_VIDEO_OPTION = "all";
const parseBooleanParam = (value, fallback) => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "off"].includes(normalized)) return false;
  return fallback;
};

const sortVideos = (videosMap) => {
  const list = Object.values(videosMap || {});
  return list
    .filter((video) => Boolean(video?.id))
    .sort((a, b) => {
      const aTime = a?.publishedAt ? new Date(a.publishedAt).getTime() : 0;
      const bTime = b?.publishedAt ? new Date(b.publishedAt).getTime() : 0;
      return bTime - aTime;
    });
};

function LiveStreaming() {
  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const currentVideoRef = useRef(null);
  const defaultAutoNext = true;
  const [autoPlayNext, setAutoPlayNext] = useState(() => {
    if (typeof window === "undefined") return defaultAutoNext;
    const params = new URLSearchParams(window.location.search || "");
    if (!params.has("autonext")) return defaultAutoNext;
    return parseBooleanParam(params.get("autonext"), defaultAutoNext);
  });
  const [randomPlayNext, setRandomPlayNext] = useState(false);
  const [isApiReady, setIsApiReady] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState(ALL_VIDEO_OPTION);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [history, setHistory] = useState([]);
  const [showControls, setShowControls] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [controlPosition, setControlPosition] = useState({ left: null, top: null });
  const [isDragging, setIsDragging] = useState(false);
  const [typeaheadQuery, setTypeaheadQuery] = useState("");
  const [isMobileView, setIsMobileView] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const controlsRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const userGestureRef = useRef(false);

  const markUserGesture = useCallback(() => {
    userGestureRef.current = true;
  }, []);

  const ensureMobileAutoplay = useCallback((player) => {
    if (!isMobileView || userGestureRef.current) return;
    if (player && typeof player.mute === "function") {
      player.mute();
      setIsMuted(true);
    }
  }, [isMobileView]);

  const getBottomNavInset = useCallback(() => {
    if (typeof window === "undefined") return 8;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    if (!isMobile) return 8;
    const rootStyles = window.getComputedStyle(document.documentElement);
    const heightValue = rootStyles.getPropertyValue("--bottom-nav-height").trim() || "64px";
    const safeValue = rootStyles.getPropertyValue("--bottom-nav-safe-area").trim() || "0px";
    const parsePx = (value) => {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const height = parsePx(heightValue);
    const safe = parsePx(safeValue);
    return height + safe + 16;
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => {
      setIsMobileView(window.matchMedia("(max-width: 900px)").matches);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    const params = new URLSearchParams(window.location.search || "");
    if (autoPlayNext === defaultAutoNext) {
      params.delete("autonext");
    } else {
      params.set("autonext", autoPlayNext ? "1" : "0");
    }
    const nextSearch = params.toString();
    const newUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
    window.history.replaceState({}, "", newUrl);
    return () => window.removeEventListener("resize", handleResize);
  }, [autoPlayNext, defaultAutoNext]);

  const playerVideos = useMemo(() => sortVideos(localVideos), []);

  const videosMap = useMemo(() => {
    const map = {};
    playerVideos.forEach((video) => {
      map[video.id] = video;
    });
    return map;
  }, [playerVideos]);

  const playlistOptions = useMemo(() => {
    const base = Array.isArray(playlistsData) ? playlistsData : [];
    return [
      { id: ALL_VIDEO_OPTION, title: "All Videos" },
      ...base.map((playlist) => ({
        id: playlist.id,
        title: playlist.title,
        videoIds: playlist.videoIds || [],
      })),
    ];
  }, []);

  const playlistVideos = useMemo(() => {
    if (selectedPlaylistId === ALL_VIDEO_OPTION) {
      return playerVideos;
    }
    const playlist = playlistOptions.find(
      (option) => option.id === selectedPlaylistId
    );
    if (!playlist || !Array.isArray(playlist.videoIds)) {
      return playerVideos;
    }
    const orderedVideos = playlist.videoIds
      .map((videoId) => videosMap[videoId])
      .filter(Boolean);
    return orderedVideos.length ? orderedVideos : playerVideos;
  }, [playerVideos, playlistOptions, selectedPlaylistId, videosMap]);

  const currentVideo = playlistVideos[currentIndex] || null;

  useEffect(() => {
    currentVideoRef.current = currentVideo;
  }, [currentVideo]);

  const registerPlayerContainer = useCallback((node) => {
    playerContainerRef.current = node;
  }, []);

  const handleNext = useCallback(() => {
    if (!playlistVideos.length) return;
    setHistory((prev) => [...prev.slice(-9), currentIndex]);
    const queueLength = playlistVideos.length;
    setCurrentIndex((prev) => {
      if (!queueLength) return 0;
      if (!randomPlayNext || queueLength <= 1) {
        return (prev + 1) % queueLength;
      }
      let nextIndex = prev;
      let guard = 0;
      while (nextIndex === prev && guard < 8) {
        nextIndex = Math.floor(Math.random() * queueLength);
        guard += 1;
      }
      if (nextIndex === prev) {
        return (prev + 1) % queueLength;
      }
      return nextIndex;
    });
  }, [playlistVideos.length, currentIndex, randomPlayNext]);

  const handleNextClick = useCallback(() => {
    markUserGesture();
    handleNext();
  }, [handleNext, markUserGesture]);

  const handleNextRef = useRef(handleNext);

  useEffect(() => {
    handleNextRef.current = handleNext;
  }, [handleNext]);

  useEffect(() => {
    document.body.classList.add("livestream-fullscreen");
    return () => {
      document.body.classList.remove("livestream-fullscreen");
    };
  }, []);

  useEffect(() => {
    const handleKeyToggle = (event) => {
      if (event.key === "Control" && !event.repeat) {
        setShowControls((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyToggle);
    return () => window.removeEventListener("keydown", handleKeyToggle);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const headerLogo = document.querySelector(".header-logo");
    if (!headerLogo) return undefined;
    const previousCursor = headerLogo.style.cursor;
    headerLogo.style.cursor = "pointer";
    const handleHeaderClick = (event) => {
      event.preventDefault();
      setShowControls((prev) => !prev);
    };
    headerLogo.addEventListener("click", handleHeaderClick);
    return () => {
      headerLogo.removeEventListener("click", handleHeaderClick);
      headerLogo.style.cursor = previousCursor;
    };
  }, []);

  useEffect(() => {
    if (!playlistVideos.length) return;
    setHistory([]);
    setCurrentIndex(Math.floor(Math.random() * playlistVideos.length));
  }, [playlistVideos]);

  useEffect(() => {
    const node = controlsRef.current;
    if (!node) return;
    setControlPosition((prev) => {
      if (prev.left !== null && prev.top !== null) return prev;
      const rect = node.getBoundingClientRect();
      const defaultLeft = Math.max(8, (window.innerWidth - rect.width) / 2);
      const defaultTop = Math.max(8, window.innerHeight - rect.height - getBottomNavInset());
      return { left: defaultLeft, top: defaultTop };
    });
  }, [showControls, isMinimized, getBottomNavInset]);

  useEffect(() => {
    let isCancelled = false;
    loadYouTubeIframeAPI()
      .then(() => {
        if (!isCancelled) {
          setIsApiReady(true);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setIsApiReady(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isApiReady) return;
    if (!playerContainerRef.current) return;
    if (playerRef.current) return;

    playerRef.current = new window.YT.Player(playerContainerRef.current, {
      width: "100%",
      height: "100%",
      playerVars: {
        autoplay: 1,
        controls: 0,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        fs: 0,
      },
      events: {
        onReady: (event) => {
          setIsPlayerReady(true);
          ensureMobileAutoplay(event.target);
          const initialId = currentVideoRef.current?.id;
          if (initialId) {
            event.target.loadVideoById(initialId);
            event.target.playVideo();
          }
        },
        onStateChange: (event) => {
          if (event.data === window.YT.PlayerState.ENDED) {
            const nextFn = handleNextRef.current;
            if (autoPlayNext && typeof nextFn === "function") {
              nextFn();
            }
          } else if (event.data === window.YT.PlayerState.PLAYING) {
            setIsPlaying(true);
          } else if (
            event.data === window.YT.PlayerState.PAUSED ||
            event.data === window.YT.PlayerState.BUFFERING
          ) {
            setIsPlaying(false);
          }
        },
      },
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [isApiReady]);

  useEffect(() => {
    if (!playerRef.current) return;
    if (!currentVideo?.id) return;
    if (!isPlayerReady) return;
    if (typeof playerRef.current.loadVideoById !== "function") return;
    playerRef.current.loadVideoById(currentVideo.id);
    ensureMobileAutoplay(playerRef.current);
    playerRef.current.playVideo();
    setIsPlaying(true);
  }, [currentVideo, isPlayerReady, ensureMobileAutoplay]);

  const handlePrevious = useCallback(() => {
    markUserGesture();
    setHistory((prev) => {
      if (!prev.length) return prev;
      const copy = [...prev];
      const lastIndex = copy.pop();
      if (typeof lastIndex === "number") {
        setCurrentIndex(lastIndex);
      }
      return copy;
    });
  }, []);

  const handlePlayPause = () => {
    markUserGesture();
    if (!playerRef.current) return;
    if (isPlaying) {
      playerRef.current.pauseVideo();
      setIsPlaying(false);
    } else {
      ensureMobileAutoplay(playerRef.current);
      playerRef.current.playVideo();
      setIsPlaying(true);
    }
  };

  const handleVolumeToggle = () => {
    markUserGesture();
    if (!playerRef.current) return;
    if (isMuted) {
      playerRef.current.unMute();
      setIsMuted(false);
    } else {
      playerRef.current.mute();
      setIsMuted(true);
    }
  };

  const handlePlaylistChange = (event) => {
    markUserGesture();
    setSelectedPlaylistId(event.target.value);
  };

  const handleVideoChange = (event) => {
    markUserGesture();
    const selectedId = event.target.value;
    const targetIndex = playlistVideos.findIndex((video) => video.id === selectedId);
    if (targetIndex === -1) return;
    setHistory((prev) => [...prev.slice(-9), currentIndex]);
    setCurrentIndex(targetIndex);
  };

  const selectPlaylistByOffset = useCallback((direction) => {
    if (!playlistOptions.length) return;
    const currentIdx = playlistOptions.findIndex((item) => item.id === selectedPlaylistId);
    const nextIdx = (currentIdx + direction + playlistOptions.length) % playlistOptions.length;
    setSelectedPlaylistId(playlistOptions[nextIdx].id);
  }, [playlistOptions, selectedPlaylistId]);

  const adjustPlaybackRate = useCallback((delta) => {
    if (!playerRef.current || typeof playerRef.current.getPlaybackRate !== "function") return;
    const currentRate = playerRef.current.getPlaybackRate();
    const nextRate = Math.min(2, Math.max(0.25, parseFloat((currentRate + delta).toFixed(2))));
    if (typeof playerRef.current.setPlaybackRate === "function") {
      playerRef.current.setPlaybackRate(nextRate);
    }
  }, []);

  const runTypeaheadSearch = useCallback((query) => {
    const lowered = query.trim().toLowerCase();
    if (!lowered) return;
    const matchIndex = playlistVideos.findIndex((video) =>
      video.title?.toLowerCase().includes(lowered)
    );
    if (matchIndex >= 0) {
      setHistory((prev) => [...prev.slice(-9), currentIndex]);
      setCurrentIndex(matchIndex);
    }
    setTypeaheadQuery("");
  }, [playlistVideos, currentIndex]);

  const handleTypeaheadSearch = useCallback((char) => {
    setTypeaheadQuery((prev) => {
      const updated = `${prev}${char}`.slice(-40);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        searchTimeoutRef.current = null;
        runTypeaheadSearch(updated);
      }, 800);
      return updated;
    });
  }, [runTypeaheadSearch]);

  const clearTypeahead = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setTypeaheadQuery("");
  }, []);

  const handleDragStart = (event) => {
    if (event.button !== 0) return;
    if (event.target.closest("button, select, option")) return;
    const node = controlsRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    dragOffsetRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    setIsDragging(true);
    event.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (event) => {
      const node = controlsRef.current;
      const rect = node ? node.getBoundingClientRect() : { width: 0, height: 0 };
      const newLeft = Math.max(
        8,
        Math.min(
          window.innerWidth - rect.width - 8,
          event.clientX - dragOffsetRef.current.x
        )
      );
      const bottomInset = getBottomNavInset();
      const newTop = Math.max(
        8,
        Math.min(
          window.innerHeight - rect.height - bottomInset,
          event.clientY - dragOffsetRef.current.y
        )
      );
      setControlPosition({ left: newLeft, top: newTop });
    };
    const stopDrag = () => setIsDragging(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", stopDrag);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", stopDrag);
    };
  }, [isDragging, getBottomNavInset]);

  useEffect(() => {
    const handleGlobalKey = (event) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (["input", "textarea", "select"].includes(activeTag || "")) return;
      switch (event.key) {
        case "ArrowRight":
        case "F9":
          event.preventDefault();
          markUserGesture();
          handleNext();
          return;
        case "ArrowLeft":
        case "F7":
          event.preventDefault();
          handlePrevious();
          return;
        case "ArrowUp":
          event.preventDefault();
          selectPlaylistByOffset(-1);
          return;
        case "ArrowDown":
          event.preventDefault();
          selectPlaylistByOffset(1);
          return;
        case "<":
        case ",":
          event.preventDefault();
          adjustPlaybackRate(-0.1);
          return;
        case ">":
        case ".":
          event.preventDefault();
          adjustPlaybackRate(0.1);
          return;
        case "Escape":
          event.preventDefault();
          clearTypeahead();
          return;
        default:
          break;
      }
      if (event.key.length === 1 && /[\w\s]/.test(event.key)) {
        handleTypeaheadSearch(event.key.toLowerCase());
      }
    };
    window.addEventListener("keydown", handleGlobalKey);
    return () => window.removeEventListener("keydown", handleGlobalKey);
  }, [handleNext, handlePrevious, selectPlaylistByOffset, adjustPlaybackRate, handleTypeaheadSearch, clearTypeahead]);

  const hasPrevious = history.length > 0;
  const controlStyle =
    controlPosition.left === null || controlPosition.top === null
      ? undefined
      : {
          left: `${controlPosition.left}px`,
          top: `${controlPosition.top}px`,
          bottom: "auto",
          transform: "translate3d(0,0,0)",
        };

  return (
    <div className="live-streaming-container">
      <SiteWatermark />
      <SEO
        title="GCC Live"
        description="Continuous video stream with visible playback controls."
      />

      <section className="youtube-embed">
        {!isPlayerReady && (
          <div className="player-placeholder" aria-live="polite">
            Preparing the stream&hellip;
          </div>
        )}
        <div className="player-wrapper">
          <div
            id="gcc-curated-player"
            ref={registerPlayerContainer}
            className="youtube-player"
            aria-label="GCC live video"
          />
        </div>
      </section>

      {showControls && (
        <div
          className={`livestream-controls ${isMinimized ? "collapsed" : ""}`}
          aria-live="polite"
          ref={controlsRef}
          style={controlStyle}
          onMouseDown={handleDragStart}
        >
          {typeaheadQuery && (
            <span className="typeahead-display" aria-live="polite">
              {typeaheadQuery.toUpperCase()}
            </span>
          )}
          {isMinimized ? (
            <button
              type="button"
              className="control-button plain-button"
              onClick={() => setIsMinimized(false)}
              aria-label="Restore controls"
            >
              <FaCross aria-hidden="true" />
            </button>
          ) : (
            <>
              <div className="control-buttons">
                <button
                  type="button"
                  className="control-button"
                  onClick={handlePrevious}
                  disabled={!hasPrevious}
                  aria-label="Play previous selection"
                >
                  <FaStepBackward aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="control-button"
                  onClick={handlePlayPause}
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? (
                    <FaPause aria-hidden="true" />
                  ) : (
                    <FaPlay aria-hidden="true" />
                  )}
                </button>
                <button
                  type="button"
                  className="control-button"
                  onClick={handleNextClick}
                  aria-label="Play next selection"
                >
                  <FaStepForward aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={`control-button${randomPlayNext ? " is-active" : ""}`}
                  onClick={() => setRandomPlayNext((prev) => !prev)}
                  aria-pressed={randomPlayNext}
                  aria-label="Toggle random play"
                  title="Random play"
                >
                  <FaRandom aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={`control-button${autoPlayNext ? " is-active" : ""}`}
                  onClick={() => setAutoPlayNext((prev) => !prev)}
                  aria-pressed={autoPlayNext}
                  aria-label="Toggle auto play next"
                  title="Auto play next"
                >
                  <FaInfinity aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="control-button"
                  onClick={handleVolumeToggle}
                  aria-label={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? (
                    <FaVolumeMute aria-hidden="true" />
                  ) : (
                    <FaVolumeUp aria-hidden="true" />
                  )}
                </button>
                <button
                  type="button"
                  className="control-button"
                  onClick={() => setIsMinimized(true)}
                  aria-label="Minimize controls"
                >
                  <FaWindowMinimize aria-hidden="true" />
                </button>
              </div>

              <label className="selector" htmlFor="playlist-picker">
                <FaListUl aria-hidden="true" />
                <select
                  id="playlist-picker"
                  value={selectedPlaylistId}
                  onChange={handlePlaylistChange}
                >
                  {playlistOptions.map((option) => (
                    <option value={option.id} key={option.id}>
                      {option.title}
                    </option>
                  ))}
                </select>
              </label>

              <label className="selector" htmlFor="video-picker">
                <FaMusic aria-hidden="true" />
                <select
                  id="video-picker"
                  value={currentVideo?.id || ""}
                  onChange={handleVideoChange}
                >
                  {playlistVideos.map((video) => (
                    <option value={video.id} key={video.id}>
                      {video.title}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default LiveStreaming;
